import { auth } from "@clerk/tanstack-react-start/server";
import { createMiddleware } from "@tanstack/react-start";

import { setSentryUser } from "~/lib/server/sentry-context";

// Minimal user context type for auth middleware
export interface AuthUser {
  id: string;
  clerkUserId: string;
  // Clerk data fetched directly when needed
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  profilePictureUrl: string | null;
  bio: string | null;
  title: string | null;
}

export async function getUser(clerkUserId: string): Promise<AuthUser> {
  const { getUserByClerkId } = await import("~/lib/server/user");
  const { getCachedClerkUser } = await import("~/lib/server/clerk-server");

  // The DB row and the Clerk profile are independent — fetch them in
  // parallel. The Clerk result is wrapped so a failure only surfaces on the
  // code path that actually needs it (both paths below handle it).
  const clerkUserPromise = getCachedClerkUser(clerkUserId).then(
    (value) => ({ ok: true as const, value }),
    (error: unknown) => ({ ok: false as const, error }),
  );
  let user = await getUserByClerkId(clerkUserId);

  if (!user) {
    // User not found by clerkUserId - try to link by email or create new user.
    // This handles cases where users have a DB record (e.g., from VIP purchase)
    // but signed up with Clerk using the same email.
    const { persistUserFromClerkProfile } = await import("~/lib/server/user");

    const clerkResult = await clerkUserPromise;
    if (!clerkResult.ok) {
      const { error } = clerkResult;
      throw new Error(
        `Failed to fetch Clerk user for linking: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
    const clerkUser = clerkResult.value;

    user = await persistUserFromClerkProfile({
      clerkUserId,
      email: clerkUser.email,
      firstName: clerkUser.firstName,
      lastName: clerkUser.lastName,
      clerkCreatedAt: clerkUser.createdAt,
    });

    return {
      id: user.id,
      clerkUserId,
      firstName: clerkUser.firstName,
      lastName: clerkUser.lastName,
      email: clerkUser.email,
      phoneNumber: user.phoneNumber,
      profilePictureUrl: clerkUser.imageUrl,
      bio: user.bio,
      title: user.title,
    };
  }

  const clerkResult = await clerkUserPromise;
  if (!clerkResult.ok) {
    const { error } = clerkResult;
    console.error(
      `Failed to fetch Clerk user ${clerkUserId}:`,
      error instanceof Error ? error.message : String(error),
    );
    // Fall back to database data if Clerk fails
    return {
      id: user.id,
      clerkUserId: user.clerkUserId || clerkUserId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      profilePictureUrl: user.profilePictureUrl,
      bio: user.bio,
      title: user.title,
    };
  }
  const clerkUser = clerkResult.value;

  return {
    id: user.id,
    clerkUserId: user.clerkUserId || clerkUserId,
    firstName: clerkUser.firstName,
    lastName: clerkUser.lastName,
    email: clerkUser.email,
    phoneNumber: user?.phoneNumber ?? null,
    profilePictureUrl: clerkUser.imageUrl,
    bio: user?.bio ?? null,
    title: user?.title ?? null,
  };
}

export const authMiddleware = createMiddleware({ type: "function" }).server<{
  user: AuthUser;
}>(async ({ next, context: _context }) => {
  try {
    const authState = await auth();
    if (!authState?.userId) {
      throw new Error("Authentication required");
    }

    const user = await getUser(authState.userId);
    setSentryUser({ id: user.id, email: user.email, type: "user" });

    return next({
      context: {
        user,
      },
    });
  } catch (error) {
    // Server function middleware should throw errors, not redirects
    // Redirects are handled at the route level in beforeLoad
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Authentication failed");
  }
});

export const authOrCoachMiddleware = createMiddleware({
  type: "function",
}).server<{
  coachId: string | undefined;
  userId: string | undefined;
}>(async ({ next, context: _context }) => {
  const { getAuthenticatedCoach, getAuthenticatedUser } =
    await import("~/lib/server/auth-helpers");

  // Coach and user are independent principals — resolve them in parallel.
  // Each is optional here: a settled rejection just means that principal
  // isn't present, so we proceed without it.
  const [coachResult, userResult] = await Promise.allSettled([
    getAuthenticatedCoach(),
    getAuthenticatedUser(),
  ]);

  const coachId =
    coachResult.status === "fulfilled" ? coachResult.value.id : undefined;
  const userId =
    userResult.status === "fulfilled" ? userResult.value.id : undefined;

  if (!coachId && !userId) {
    throw new Error(
      "Authentication required: Not authenticated as a Coach or a User.",
    );
  }

  // Tag the Sentry scope with whichever principal we resolved. Prefer coach
  // since coach-authenticated flows are the less common (more debuggable) path.
  if (coachId) {
    setSentryUser({ id: coachId, type: "coach" });
  } else if (userId) {
    setSentryUser({ id: userId, type: "user" });
  }

  return next({
    context: {
      coachId: coachId,
      userId: userId,
    },
  });
});
